/**
 * ============================================================================
 *  CENTRAL CONFIG — the single source of truth for every tunable value.
 * ============================================================================
 *
 *  THIS IS THE FILE YOU EDIT TO CHANGE HOW THE GAME FEELS.
 *
 *  Every number that affects gameplay, camera, bots, input, ads, etc. lives
 *  here. Nothing in the game logic hardcodes a tunable; it all reads from a
 *  `GameConfig` value that is threaded through the simulation. Want to change
 *  the arena size, the grow rate, the ad length from 10s to 5s, the bot
 *  count — change ONE number here and you are done.
 *
 *  Feature on/off switches live in a separate file: ./features.ts
 *
 *  The config is a plain data object (no logic), so it can be serialized,
 *  sent over the network, A/B tested, or swapped at runtime later.
 */

export interface WorldConfig {
  /** Arena dimensions in world units. The arena is a bounded rectangle. */
  width: number;
  height: number;
  /** Radius of a freshly spawned player. */
  startSize: number;
  /** Hard floor on radius (shrinking can never go below this). */
  minSize: number;
  /**
   * Hard ceiling on radius (growing/eating can never exceed this). Raised high
   * and paired with diminishing grow returns (actions.grow.diminishingExponent)
   * so reaching the cap is a genuine grind — most players settle well below it,
   * and being near-max is rare/earned. This is the single dial for the ceiling.
   */
  maxSize: number;
  /**
   * Spawn safety: target *surface* gap (world units) to keep between a freshly
   * spawned blob and the nearest blob big enough to eat it. Measured edge-to-
   * edge so it scales correctly now that predators can be very large.
   */
  spawnClearance: number;
  /** Fixed simulation timestep in seconds. The sim always advances in these. */
  fixedTimestep: number;
  /** Safety cap: max sim steps processed per frame (prevents spiral of death). */
  maxStepsPerFrame: number;
}

export interface MovementConfig {
  /** How quickly velocity approaches the desired velocity (1 = instant). */
  acceleration: number;
  /** Speed (units/sec) at the reference size. */
  baseSpeed: number;
  /** Speed floor so the biggest blobs still creep instead of freezing. */
  minSpeed: number;
  /** Size used as the "1.0x speed" reference point. */
  referenceSize: number;
  /**
   * Exponent of the size->speed falloff. speed = baseSpeed * (ref/size)^exp,
   * clamped to [minSpeed, baseSpeed]. Higher = bigger blobs slow down faster.
   */
  sizeSpeedExponent: number;
}

export interface ActionGrowConfig {
  /**
   * BASE fraction of current mass added per consumed click, at small sizes.
   * The effective gain is this multiplied by a falloff that fades to 0 as size
   * approaches maxSize (see `diminishingExponent`), so clicking is punchy when
   * you are small and a grind when you are big. The early game is unchanged.
   */
  massFractionPerPulse: number;
  /**
   * Shape of the diminishing-returns curve. Effective per-click mass gain is
   * `massFractionPerPulse * (1 - progress)^diminishingExponent`, where
   * `progress` runs 0..1 from minSize to maxSize. Higher = each click yields a
   * smaller fraction sooner (steeper grind earlier, wider size spread); lower =
   * stays punchy deeper and only chokes very near the cap. 1.0 = headroom falls
   * off linearly. THIS is the dial for "how hard is it to get big".
   */
  diminishingExponent: number;
  /** Click-rate cap (clicks/sec). Doubles as the first anti-autoclicker guard. */
  maxPerSec: number;
  /** Token-bucket burst size (max clicks that can be "banked"). */
  burst: number;
}

export interface ActionShrinkConfig {
  /** Fraction of current mass removed per consumed click. */
  massFractionPerPulse: number;
  /** Click-rate cap (clicks/sec). */
  maxPerSec: number;
  /** Token-bucket burst size. */
  burst: number;
  /** Speed multiplier granted briefly after shrinking (the escape boost). */
  boostMultiplier: number;
  /** How long the boost lasts after the last shrink, in seconds. */
  boostDurationSec: number;
}

export interface ActionsConfig {
  grow: ActionGrowConfig;
  shrink: ActionShrinkConfig;
}

export interface EatConfig {
  /** Attacker must be at least this many times the prey's size to eat it. */
  sizeRatio: number;
  /**
   * How deep the overlap must be: prey is eaten when
   * distance <= attackerSize - preySize * overlapFactor.
   */
  overlapFactor: number;
  /** Fraction of the prey's mass converted into the attacker's growth. */
  massConversion: number;
}

export interface BotsConfig {
  /** Target population of bots kept alive in the arena. */
  count: number;
  /** Smallest size a freshly spawned bot can have. */
  minSpawnSize: number;
  /** Largest size a freshly spawned bot can have. */
  maxSpawnSize: number;
  /** Average time between AI re-decisions (seconds) — the "reaction delay". */
  reactionTime: number;
  /** Random +/- jitter added to reactionTime so bots aren't synchronized. */
  reactionJitter: number;
  /** Base perception radius; effective range also scales with bot size. */
  perceptionBase: number;
  /** Perception range added per unit of bot size. */
  perceptionPerSize: number;
  /** Simulated clicks/sec a bot performs while growing (hunting/safe). */
  growClicksPerSec: number;
  /** Simulated clicks/sec a bot performs while shrinking (fleeing). */
  shrinkClicksPerSec: number;
  /** Seconds a wandering bot keeps its current heading before picking a new one. */
  wanderInterval: number;
  /** A threat must be at least this size ratio bigger before a bot flees. */
  fleeSizeRatio: number;
}

export interface InputConfig {
  /**
   * Desktop: pointer distance (px) from the player at which movement throttle
   * reaches 1.0. Inside `deadzonePx` the player does not move.
   */
  controlRadiusPx: number;
  deadzonePx: number;
}

export interface CameraConfig {
  /** Zoom at the reference size. */
  baseZoom: number;
  /** Auto-zoom (size-based) clamp bounds. */
  minZoom: number;
  maxZoom: number;
  /** Exponent of the size->zoom falloff (bigger player => more zoomed out). */
  zoomExponent: number;
  /** Size used as the "baseZoom" reference point. */
  referenceSize: number;
  /** How fast the camera position/zoom eases toward its target (per second). */
  followLerp: number;
  zoomLerp: number;

  // --- Manual (player-driven) zoom; only active when FEATURES.manualZoom ---
  /**
   * How manual zoom combines with the size-based auto-zoom:
   *  - 'relative' (default): the size-based auto-zoom sets the baseline from the
   *    player's size, and manual pinch/scroll nudges in/out AROUND that baseline
   *    by a multiplier in [manualNudgeMin, manualNudgeMax]. Because the bound is
   *    relative to the baseline, the nudge is always live — even a max-size
   *    player (whose baseline is already pulled far out) can still nudge in/out.
   *  - 'absolute': the player sets the zoom directly within [manualMinZoom,
   *    manualMaxZoom], OVERRIDING the size-based auto-zoom. (Note: in this mode a
   *    neutral, un-nudged camera ignores player size entirely.)
   * Switching modes is this one-line change.
   */
  manualZoomMode: 'absolute' | 'relative';
  /** RELATIVE mode: most the player can zoom OUT, as a multiple of the auto-zoom
   *  baseline (e.g. 0.6 = down to 60% of the baseline zoom). Widen the gap to
   *  [manualNudgeMin, manualNudgeMax] to give the player more zoom adjustment. */
  manualNudgeMin: number;
  /** RELATIVE mode: most the player can zoom IN, as a multiple of the baseline. */
  manualNudgeMax: number;
  /** ABSOLUTE mode: hard lower bound for manual zoom (most zoomed-OUT). */
  manualMinZoom: number;
  /** ABSOLUTE mode: hard upper bound for manual zoom (most zoomed-IN). */
  manualMaxZoom: number;
  /** Zoom multiplier applied per scroll-wheel notch (pinch uses the raw finger
   *  distance ratio). Higher = more sensitive wheel zoom. */
  manualZoomStep: number;
}

/** Which screen corner a corner-anchored overlay (the minimap) sits in. */
export type MinimapCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface MinimapConfig {
  /** Screen corner for the minimap on desktop. (On touch devices the app uses a
   *  corner clear of the joystick/buttons — see README.) */
  corner: MinimapCorner;
  /** Minimap box side length in CSS px (desktop). */
  size: number;
  /** Minimap box side length on touch devices (smaller, stays out of the way). */
  mobileSize: number;
  /** Gap (px) between the minimap box and the screen edge. */
  margin: number;
  /** Box background fill — include an alpha channel to set the opacity. */
  background: string;
  /** Box border colour. */
  border: string;
  /** Player dot colour (the bright, prominent dot). */
  playerColor: string;
  /** Player dot radius in minimap px. */
  playerDotPx: number;
  /** Colour for large "threat" blobs (size >= sizeThreshold). */
  threatColor: string;
  /** Threat dot radius in minimap px. */
  threatDotPx: number;
  /** Colour for small blobs (size < sizeThreshold) when shown faintly. */
  smallColor: string;
  /** Small dot radius in minimap px. */
  smallDotPx: number;
  /** Colour of the current-viewport rectangle outline. */
  viewportColor: string;
  /**
   * Which entities to plot:
   *  - 'all':       large blobs prominent + small blobs faint (full awareness).
   *  - 'threshold': only blobs with size >= sizeThreshold (plus the player).
   */
  showPolicy: 'all' | 'threshold';
  /** Size at/above which a blob is drawn as a prominent threat. */
  sizeThreshold: number;
}

export interface GameConfig {
  world: WorldConfig;
  movement: MovementConfig;
  actions: ActionsConfig;
  eat: EatConfig;
  bots: BotsConfig;
  input: InputConfig;
  camera: CameraConfig;
  minimap: MinimapConfig;
  spatial: SpatialConfig;
  leaderboard: LeaderboardConfig;
  ads: AdsConfig;
  scoring: ScoringConfig;
}

export interface SpatialConfig {
  /** Grid cell size (world units) for the spatial hash used in eat checks. */
  cellSize: number;
}

export interface LeaderboardConfig {
  /** How many entries to display in the HUD leaderboard. */
  size: number;
}

export interface AdsConfig {
  /** Show a rewarded ad on every Nth death (1 = every death). */
  showEveryNthDeath: number;
  /** Total ad length in seconds. Change this single value to retune ad length. */
  lengthSec: number;
  /** Ad becomes skippable after this many seconds. */
  skipAfterSec: number;
}

export interface ScoringConfig {
  /**
   * Run score = peakMassWeight*peakMass + massEatenWeight*massEaten
   *           + killWeight*kills + survivalWeight*secondsAlive.
   * Folding in continuous factors (mass eaten, kills, time alive) keeps the
   * leaderboard ordered even when several blobs sit at the size cap, so
   * maxed-out players no longer tie. All weights are tunable here.
   */
  /** Weight on peak mass reached (peakSize^2). Dominant term — keeps "bigger is better". */
  peakMassWeight: number;
  /** Weight on total prey mass eaten this life. Rewards hunting beyond raw size. */
  massEatenWeight: number;
  /** Flat points per kill. Breaks ties between similarly-sized hunters. */
  killWeight: number;
  /** Points per second survived. A strictly-increasing, continuous tiebreaker. */
  survivalWeight: number;
}

/**
 * The default, shipped tuning. These values are chosen so the game feels good
 * to play out of the box: fast, readable growth; shrinking that genuinely saves
 * you; bots that hunt and flee believably; a camera that keeps threats visible.
 */
export const DEFAULT_CONFIG: GameConfig = {
  world: {
    // Enlarged from 4200 (≈+178% area) to open up the space; bots.count is
    // raised in step (see below) so density lands "not packed, not empty".
    width: 7000,
    height: 7000,
    startSize: 24,
    minSize: 14,
    // Raised from 420. Combined with diminishing grow returns below, the cap is
    // a genuine grind to reach, so most blobs spread out below it. Move this one
    // value to change the ceiling. (Speed at this size is ~67 px/s — slow, not
    // frozen — and the camera floor below keeps a max blob's whole view visible.)
    maxSize: 900,
    spawnClearance: 420,
    fixedTimestep: 1 / 60,
    maxStepsPerFrame: 5,
  },
  movement: {
    acceleration: 11,
    baseSpeed: 330,
    minSpeed: 66,
    referenceSize: 24,
    sizeSpeedExponent: 0.44,
  },
  actions: {
    grow: {
      massFractionPerPulse: 0.05,
      // 1.3 = a bit steeper than linear: early game is untouched, but per-click
      // gain falls off through the upper-mid range and collapses near the cap,
      // so reaching max is a grind. Raise to grind sooner/harder; lower (~0.8)
      // to stay punchy deeper. This is THE dial for diminishing growth.
      diminishingExponent: 1.3,
      maxPerSec: 12,
      burst: 14,
    },
    shrink: {
      massFractionPerPulse: 0.06,
      maxPerSec: 10,
      burst: 12,
      boostMultiplier: 1.7,
      boostDurationSec: 1.1,
    },
  },
  eat: {
    sizeRatio: 1.15,
    overlapFactor: 0.4,
    massConversion: 1.0,
  },
  bots: {
    // Raised from 26 alongside the larger arena. Net density ≈ 1.2M units²/bot
    // (vs ≈0.68M before) — more room to maneuver, still ~7 blobs visible at max
    // zoom-out, so nothing feels barren. Tune this with world.width/height.
    count: 40,
    minSpawnSize: 14,
    maxSpawnSize: 120,
    reactionTime: 0.35,
    reactionJitter: 0.15,
    perceptionBase: 320,
    perceptionPerSize: 4,
    growClicksPerSec: 6,
    shrinkClicksPerSec: 9,
    wanderInterval: 2.5,
    fleeSizeRatio: 1.15,
  },
  input: {
    controlRadiusPx: 180,
    deadzonePx: 16,
  },
  camera: {
    baseZoom: 1.0,
    // Zoom-out floor (raised 0.30 -> 0.42) so you can NEVER see the whole 7000
    // arena at once. At max zoom-out the view spans ≈ cssW/0.42 world units
    // (~3800 ≈ 54% of arena width on a ~1600px screen) — generous but bounded.
    // Governs the size-based auto-zoom and the 'relative' manual baseline.
    minZoom: 0.42,
    maxZoom: 1.25,
    zoomExponent: 0.45,
    referenceSize: 24,
    followLerp: 7,
    zoomLerp: 3.5,
    // Manual zoom (FEATURES.manualZoom). 'relative' (default) rides on the
    // size-based auto-zoom: the auto-zoom sets the baseline, and the player
    // nudges within [manualNudgeMin, manualNudgeMax] times that baseline — so
    // auto-zoom is what you see when you don't pinch, and the nudge stays live
    // at every size. manualNudgeMin 0.6 keeps a small player from scouting the
    // whole arena (1.0 baseline * 0.6 still shows well under half the map).
    manualZoomMode: 'relative',
    manualNudgeMin: 0.6,
    manualNudgeMax: 1.6,
    // ABSOLUTE-mode-only hard bounds (unused while mode is 'relative').
    manualMinZoom: 0.42,
    manualMaxZoom: 1.6,
    manualZoomStep: 1.12,
  },
  minimap: {
    // Desktop corner. On touch devices the app overrides to a corner clear of
    // the joystick/buttons (see README). Change `corner`/`size` to move/resize.
    corner: 'bottom-right',
    size: 184,
    mobileSize: 116,
    margin: 16,
    background: 'rgba(10, 13, 18, 0.62)',
    border: 'rgba(56, 189, 248, 0.45)',
    playerColor: '#ffffff',
    playerDotPx: 4,
    threatColor: '#f87171',
    threatDotPx: 3,
    smallColor: 'rgba(148, 163, 184, 0.7)',
    smallDotPx: 1.6,
    viewportColor: 'rgba(56, 189, 248, 0.9)',
    // 'all' = large prominent + small faint; 'threshold' = only blobs >= sizeThreshold.
    showPolicy: 'all',
    sizeThreshold: 120,
  },
  spatial: {
    cellSize: 220,
  },
  leaderboard: {
    size: 8,
  },
  ads: {
    // Defaults requested: not every replay, skippable, 10s long.
    showEveryNthDeath: 3,
    lengthSec: 10,
    skipAfterSec: 5,
  },
  scoring: {
    // peakMass dominates (0.1 keeps the familiar "mass/10" scale), while the
    // continuous terms differentiate players who otherwise tie at the cap.
    peakMassWeight: 0.1,
    massEatenWeight: 0.05,
    killWeight: 25,
    survivalWeight: 2,
  },
};
